import * as React from "react"
import { router } from "@inertiajs/react"
import { FileArchive, FileJson2, FolderInput, FolderPlus, Layers3, Upload } from "lucide-react"
import { Button } from "@ui/Button"
import { Combobox } from "@ui/Combobox"
import { Dialog } from "@ui/Dialog"
import { Field } from "@ui/Field"
import { Input } from "@ui/Input"
import { RadioGroup, RadioItem } from "@ui/RadioGroup"
import { Select } from "@ui/Select"
import { cn } from "@lib/cn"
import type { Folder, Project, SharedPageProps } from "@lib/inertia-types"

export type ImportFormat = "postman" | "bruno"
type DestinationMode = "root" | "new_folder" | "existing_folder"

export function ImportCollectionDialog({
  open,
  onOpenChange,
  project,
  folders,
  initialFormat = "postman",
  reloadOnly,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  project: Project
  folders: Folder[]
  initialFormat?: ImportFormat
  reloadOnly: string[]
}) {
  const [format, setFormat] = React.useState<ImportFormat>(initialFormat)
  const [destinationMode, setDestinationMode] = React.useState<DestinationMode>("new_folder")
  const [folderId, setFolderId] = React.useState("")
  const [folderName, setFolderName] = React.useState("")
  const [file, setFile] = React.useState<File | null>(null)
  const [submitting, setSubmitting] = React.useState(false)
  const fileInput = React.useRef<HTMLInputElement>(null)

  React.useEffect(() => {
    if (!open) return
    setFormat(initialFormat)
    setDestinationMode("new_folder")
    setFolderId("")
    setFolderName("")
    setFile(null)
    setSubmitting(false)
    if (fileInput.current) fileInput.current.value = ""
  }, [open, initialFormat])

  const folderOptions = React.useMemo(() => folderBreadcrumbOptions(folders), [folders])
  const selectedFolder = folderOptions.find((option) => option.value === folderId)
  const canSubmit = Boolean(file) && (destinationMode !== "existing_folder" || Boolean(folderId))

  function changeFormat(value: ImportFormat) {
    setFormat(value)
    setFile(null)
    if (fileInput.current) fileInput.current.value = ""
  }

  function submit(event: React.FormEvent) {
    event.preventDefault()
    if (!file || !canSubmit) return

    setSubmitting(true)
    router.post(
      `/projects/${project.slug}/import`,
      {
        file,
        format,
        destination_mode: destinationMode,
        folder_id: destinationMode === "existing_folder" ? folderId : undefined,
        folder_name: destinationMode === "new_folder" ? folderName.trim() : undefined,
      },
      {
        forceFormData: true,
        preserveScroll: true,
        only: reloadOnly,
        onSuccess: (page) => {
          if (!(page.props as SharedPageProps).flash?.alert) onOpenChange(false)
        },
        onFinish: () => setSubmitting(false),
      },
    )
  }

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="md">
        <form onSubmit={submit}>
          <Dialog.Header>
            <Dialog.Title>Import collection</Dialog.Title>
            <Dialog.Description>
              Add a Postman or Bruno request tree to {project.name}.
            </Dialog.Description>
          </Dialog.Header>

          <Dialog.Body className="flex flex-col gap-5 pb-3">
            <div className="grid gap-4 sm:grid-cols-[10rem_1fr]">
              <Field label="Format">
                <Select.Root value={format} onValueChange={(value) => value && changeFormat(value as ImportFormat)}>
                  <Select.Trigger>
                    <Select.Value />
                  </Select.Trigger>
                  <Select.Content>
                    <Select.Item value="postman">Postman v2.1</Select.Item>
                    <Select.Item value="bruno">Bruno</Select.Item>
                  </Select.Content>
                </Select.Root>
              </Field>

              <Field
                label="Collection file"
                required
                description={format === "postman" ? "Postman Collection v2.1 JSON" : "Bruno collection ZIP"}
              >
                <FilePicker
                  format={format}
                  file={file}
                  inputRef={fileInput}
                  onFileChange={setFile}
                />
              </Field>
            </div>

            <div>
              <p className="mb-2 text-small font-medium text-cloverhound-gray-800 dark:text-cloverhound-gray-50">
                Import destination
              </p>
              <RadioGroup
                value={destinationMode}
                onValueChange={(value) => setDestinationMode(value as DestinationMode)}
                className="grid gap-2 sm:grid-cols-3"
              >
                <DestinationOption
                  value="new_folder"
                  icon={<FolderPlus className="size-4" aria-hidden="true" />}
                  title="New folder"
                  description="Keep the collection together"
                  selected={destinationMode === "new_folder"}
                />
                <DestinationOption
                  value="root"
                  icon={<Layers3 className="size-4" aria-hidden="true" />}
                  title="Project root"
                  description="Add top-level items directly"
                  selected={destinationMode === "root"}
                />
                <DestinationOption
                  value="existing_folder"
                  icon={<FolderInput className="size-4" aria-hidden="true" />}
                  title="Existing folder"
                  description="Place items in a folder"
                  selected={destinationMode === "existing_folder"}
                />
              </RadioGroup>
            </div>

            {destinationMode === "new_folder" && (
              <Field
                label="Folder name"
                description="Leave blank to use the collection name. Duplicate names receive a numeric suffix."
              >
                <Input
                  value={folderName}
                  onChange={(event) => setFolderName(event.target.value)}
                  placeholder="Use collection name"
                  autoFocus
                />
              </Field>
            )}

            {destinationMode === "existing_folder" && (
              <Field
                label="Folder"
                required
                description={
                  selectedFolder
                    ? `Top-level collection items will be added directly inside ${selectedFolder.label}.`
                    : "Choose where to add the collection's top-level items."
                }
              >
                <Combobox
                  options={folderOptions}
                  value={folderId}
                  onChange={setFolderId}
                  placeholder="Choose a folder…"
                  searchPlaceholder="Search folders…"
                  emptyMessage="This project has no folders yet."
                />
              </Field>
            )}
          </Dialog.Body>

          <Dialog.Footer>
            <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={!canSubmit}>
              <Upload className="size-4" aria-hidden="true" />
              Import collection
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  )
}

function FilePicker({
  format,
  file,
  inputRef,
  onFileChange,
  ...inputProps
}: Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "accept" | "onChange"> & {
  format: ImportFormat
  file: File | null
  inputRef: React.RefObject<HTMLInputElement | null>
  onFileChange: (file: File | null) => void
}) {
  return (
    <div className="relative rounded-md focus-within:ring-2 focus-within:ring-cloverhound-indigo-500/30">
      <input
        ref={inputRef}
        type="file"
        accept={format === "postman" ? ".json,application/json" : ".zip,application/zip"}
        onChange={(event) => onFileChange(event.target.files?.[0] ?? null)}
        className="absolute inset-0 z-10 cursor-pointer opacity-0"
        {...inputProps}
      />
      <div
        className={cn(
          "flex h-8 items-center gap-2 rounded-md border px-3 text-small",
          "border-cloverhound-gray-300 bg-white dark:border-cloverhound-gray-800 dark:bg-cloverhound-gray-900",
          "transition-colors hover:border-cloverhound-gray-400 dark:hover:border-cloverhound-gray-700",
        )}
      >
        {format === "postman" ? (
          <FileJson2 className="size-4 shrink-0 text-cloverhound-orange-500" aria-hidden="true" />
        ) : (
          <FileArchive className="size-4 shrink-0 text-cloverhound-indigo-500" aria-hidden="true" />
        )}
        <span className={cn("truncate", !file && "text-cloverhound-gray-600")}>
          {file?.name ?? "Choose a file…"}
        </span>
      </div>
    </div>
  )
}

function DestinationOption({
  value,
  icon,
  title,
  description,
  selected,
}: {
  value: DestinationMode
  icon: React.ReactNode
  title: string
  description: string
  selected: boolean
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer gap-2.5 rounded-lg border p-3 transition-colors",
        selected
          ? "border-cloverhound-indigo-400 bg-cloverhound-indigo-50/70 dark:border-cloverhound-indigo-500 dark:bg-cloverhound-indigo-500/10"
          : "border-cloverhound-gray-250 hover:border-cloverhound-gray-400 dark:border-cloverhound-gray-800 dark:hover:border-cloverhound-gray-700",
      )}
    >
      <RadioItem value={value} className="mt-0.5" />
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 text-small font-medium text-cloverhound-gray-900 dark:text-cloverhound-gray-50">
          <span className={selected ? "text-cloverhound-indigo-600 dark:text-cloverhound-indigo-300" : "text-cloverhound-gray-500"}>
            {icon}
          </span>
          {title}
        </span>
        <span className="mt-0.5 block text-mini leading-snug text-cloverhound-gray-600">{description}</span>
      </span>
    </label>
  )
}

function folderBreadcrumbOptions(folders: Folder[]) {
  const byId = new Map(folders.map((folder) => [folder.id, folder]))

  return folders.map((folder) => {
    const names = [folder.name]
    const visited = new Set([folder.id])
    let parent = folder.parent_id ? byId.get(folder.parent_id) : undefined

    while (parent && !visited.has(parent.id)) {
      names.unshift(parent.name)
      visited.add(parent.id)
      parent = parent.parent_id ? byId.get(parent.parent_id) : undefined
    }

    return {
      value: String(folder.id),
      label: names.join(" / "),
      keywords: names,
    }
  })
}
